import React, { useEffect, useRef, useState } from "react";
import API from "../../Network/API";
import { toast } from "react-toastify";

/**
 * RecordingPlayerModal
 *
 * Fetches the 7-day pre-signed URL from the backend on open, then streams
 * the MP4 directly via the browser's native video player.
 *
 * Props:
 *   isOpen        {boolean}   — render / hide the modal
 *   onClose       {function}  — dismiss callback
 *   roomName      {string}    — livekit_room_name [legacy fallback]
 *   livekitRoom   {string}    — preferred: livekit_room_name
 *   meetingTitle  {string}    — displayed in header
 *   sizeDisplay   {string}    — pre-formatted file size string
 *   endedAt       {string}    — ISO8601 timestamp
 *
 * DEV → PRODUCTION: no changes needed.
 */
export default function RecordingPlayerModal({
  isOpen,
  onClose,
  roomName,
  livekitRoom,
  meetingTitle,
  sizeDisplay: initialSize,
  endedAt,
}) {
  const videoRef = useRef(null);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [expiresAt,    setExpiresAt]    = useState(null);
  const [duration,     setDuration]     = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [isPlaying,    setIsPlaying]    = useState(false);
  const [sizeDisplay,  setSizeDisplay]  = useState(initialSize);

  // ── Share state ──────────────────────────────────────
  const [shareToken, setShareToken]     = useState(null);
  const [isPublic, setIsPublic]         = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const shareUrl = shareToken ? `${window.location.origin}/recording/${shareToken}` : null;

  const room = livekitRoom || roomName;

  // Sanitize title: if it's a raw room ID (short, alphanumeric, no spaces) show generic label
  const looksLikeRoomId = (s) => !!s && s.length <= 14 && /^[a-zA-Z0-9-]+$/.test(s) && !s.includes(" ");
  const displayTitle = meetingTitle && !looksLikeRoomId(meetingTitle)
    ? meetingTitle
    : "Meeting Recording";

  // Fetch pre-signed URL each time modal opens
  useEffect(() => {
    if (!isOpen || !room) return;
    setRecordingUrl(null);
    setError(null);
    setIsPlaying(false);
    setLoading(true);

    API.get(`/v1/meetings/${room}/recording`)
      .then((res) => {
        const data = res.data;
        if (data && data.success && data.recording_url) {
          setRecordingUrl(data.recording_url);
          setExpiresAt(data.expires_at);
          setDuration(data.duration_seconds);
          setSizeDisplay(data.size_display || initialSize);
          setShareToken(data.share_token || null);
          setIsPublic(data.is_public || false);
        } else if (data && !data.success) {
          setError(data.message || "Recording not available.");
        } else {
          setError("Unable to retrieve recording URL.");
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        setError(
          status === 403 ? "You don't have access to this recording."
          : status === 404 ? "Recording not found or still processing."
          : "Failed to load recording. Please try again."
        );
      })
      .finally(() => setLoading(false));
  }, [isOpen, room]);

  // Pause + reset video when modal closes
  useEffect(() => {
    if (!isOpen && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    if (isOpen) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handlePlay = () => {
    setIsPlaying(true); // mount video first
    // play() may throw NotAllowedError in some browsers; that's fine, controls will appear
    setTimeout(() => { videoRef.current?.play().catch(() => {}); }, 50);
  };

  const formatDuration = (secs) => {
    if (!secs) return null;
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const formattedDate = endedAt
    ? new Date(endedAt).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : null;

  return (
    <>
      {/* ── Backdrop ── */}
      <div
        id="recording-modal-backdrop"
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.85)",
          backdropFilter: "blur(8px)",
          zIndex: 1060,
          animation: "fadeInBackdrop 0.2s ease",
        }}
      />

      {/* ── Modal Panel ── */}
      <div
        id="recording-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`Recording: ${displayTitle}`}
        style={{
          position: "fixed",
          top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(860px, 94vw)",
          maxHeight: "90vh",
          background: "#111",
          borderRadius: "16px",
          zIndex: 1061,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 32px 80px rgba(0,0,0,0.7)",
          animation: "slideUpModal 0.25s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 20px",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(255,255,255,0.04)",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0, flex: 1 }}>
            <span style={{
              background: "#ef4444", color: "#fff",
              fontSize: "10px", fontWeight: "700",
              letterSpacing: "0.8px", padding: "3px 8px",
              borderRadius: "4px", flexShrink: 0,
            }}>
              ● REC
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                color: "#fff", fontWeight: "600", fontSize: "15px",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {displayTitle}
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "2px", flexWrap: "wrap" }}>
                {formattedDate && (
                  <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "11px" }}>
                    📅 {formattedDate}
                  </span>
                )}
                {duration && (
                  <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "11px" }}>
                    ⏱ {formatDuration(duration)}
                  </span>
                )}
                {sizeDisplay && (
                  <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "11px" }}>
                    💾 {sizeDisplay}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Download button in header (shows when URL ready) + Share controls + Close button */}
          <div style={{ display: "flex", gap: "8px", flexShrink: 0, alignItems: "center" }}>
            {recordingUrl && (
              <a
                id="recording-download-btn"
                href={recordingUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                title="Download recording"
                style={{
                  background: "rgba(255,255,255,0.1)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.8)",
                  padding: "6px 12px",
                  fontSize: "12px", fontWeight: "500",
                  display: "flex", alignItems: "center", gap: "6px",
                  textDecoration: "none",
                  transition: "background 0.15s",
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.18)")}
                onMouseOut={(e) =>  (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
              >
                <i className="fa-solid fa-download" style={{ fontSize: "11px" }} />
                Download
              </a>
            )}

            {/* ── Share controls (host-only, shown when recording is available) ── */}
            {recordingUrl && (
              <>
                {/* Toggle: make recording public / private */}
                <button
                  id="recording-share-toggle-btn"
                  disabled={shareLoading}
                  title={isPublic ? "Disable public sharing" : "Enable public sharing"}
                  onClick={async () => {
                    setShareLoading(true);
                    try {
                      const res = await API.post(`/v1/meetings/${room}/recording/toggle-public`, { enabled: !isPublic });
                      if (res.data?.success) {
                        setIsPublic(res.data.is_public);
                        setShareToken(res.data.share_token);
                        toast.success(res.data.is_public ? '🔗 Recording is now public' : '🔒 Recording is now private');
                      }
                    } catch { toast.error('Failed to update sharing settings'); }
                    setShareLoading(false);
                  }}
                  style={{
                    background: isPublic ? "rgba(34,197,94,0.2)" : "rgba(255,255,255,0.08)",
                    border: `1px solid ${isPublic ? "rgba(34,197,94,0.5)" : "rgba(255,255,255,0.15)"}`,
                    borderRadius: "8px", padding: "6px 12px", fontSize: "12px", fontWeight: "500",
                    color: isPublic ? "#4ade80" : "rgba(255,255,255,0.7)",
                    cursor: shareLoading ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center", gap: "6px", transition: "all 0.15s",
                  }}
                >
                  <i className={`fa-solid ${isPublic ? "fa-lock-open" : "fa-lock"}`} style={{ fontSize: "11px" }} />
                  {isPublic ? "Public" : "Private"}
                </button>

                {/* Copy platform share link (only if public and token exists) */}
                {isPublic && shareUrl && (
                  <button
                    id="recording-copy-link-btn"
                    title="Copy share link"
                    onClick={() => {
                      navigator.clipboard.writeText(shareUrl);
                      toast.success('📋 Share link copied!');
                    }}
                    style={{
                      background: "rgba(59,130,246,0.2)",
                      border: "1px solid rgba(59,130,246,0.4)",
                      borderRadius: "8px", padding: "6px 12px", fontSize: "12px", fontWeight: "500",
                      color: "#93c5fd", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: "6px", transition: "all 0.15s",
                    }}
                  >
                    <i className="fa-solid fa-link" style={{ fontSize: "11px" }} />
                    Copy Link
                  </button>
                )}

                {/* Revoke: rotate token, disable all old links */}
                {shareToken && (
                  <button
                    id="recording-revoke-btn"
                    disabled={shareLoading}
                    title="Revoke share link — all existing links become invalid"
                    onClick={async () => {
                      if (!window.confirm('Revoke the share link? Anyone who has the current link will lose access immediately.')) return;
                      setShareLoading(true);
                      try {
                        const res = await API.post(`/v1/meetings/${room}/recording/revoke`);
                        if (res.data?.success) {
                          setShareToken(null);
                          setIsPublic(false);
                          toast.success('🚫 Share link revoked');
                        }
                      } catch { toast.error('Failed to revoke link'); }
                      setShareLoading(false);
                    }}
                    style={{
                      background: "rgba(239,68,68,0.1)",
                      border: "1px solid rgba(239,68,68,0.3)",
                      borderRadius: "8px", padding: "6px 12px", fontSize: "12px", fontWeight: "500",
                      color: "rgba(248,113,113,0.9)", cursor: shareLoading ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "6px", transition: "all 0.15s",
                    }}
                  >
                    <i className="fa-solid fa-ban" style={{ fontSize: "11px" }} />
                    Revoke
                  </button>
                )}
              </>
            )}
            <button
              id="recording-modal-close"
              onClick={onClose}
              title="Close (Esc)"
              style={{
                background: "rgba(255,255,255,0.1)", border: "none",
                borderRadius: "8px", color: "rgba(255,255,255,0.7)",
                width: "34px", height: "34px", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", transition: "background 0.15s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.18)")}
              onMouseOut={(e) =>  (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
            >
              <i className="fa-solid fa-xmark" style={{ fontSize: "15px" }} />
            </button>
          </div>
        </div>

        {/* ── Content area ── */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#000", minHeight: "320px" }}>

          {/* Loading */}
          {loading && (
            <div style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
              flexDirection: "column", color: "rgba(255,255,255,0.5)",
            }}>
              <div style={{
                width: "44px", height: "44px",
                border: "3px solid rgba(255,255,255,0.1)",
                borderTopColor: "#ef4444", borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                marginBottom: "12px",
              }} />
              <div style={{ fontSize: "13px" }}>Loading recording…</div>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
              flexDirection: "column", textAlign: "center", padding: "32px 24px",
            }}>
              <div style={{ fontSize: "36px", marginBottom: "12px" }}>⚠️</div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", marginBottom: "16px" }}>
                {error}
              </div>
              <button
                onClick={() => { setError(null); setLoading(true); }}
                style={{
                  background: "#ef4444", color: "#fff", border: "none",
                  borderRadius: "8px", padding: "8px 20px",
                  fontSize: "13px", cursor: "pointer", fontWeight: "500",
                }}
              >
                Try Again
              </button>
            </div>
          )}

          {/* Pre-play state: show thumbnail placeholder + big central play button */}
          {!loading && !error && recordingUrl && !isPlaying && (
            <div
              data-testid="play-overlay"
              onClick={handlePlay}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                background: "linear-gradient(135deg, #1a1a1a 0%, #111 100%)",
                minHeight: "320px",
                userSelect: "none",
              }}
            >
              {/* Decorative waveform lines */}
              <div style={{ position: "absolute", bottom: "60px", left: 0, right: 0, display: "flex", alignItems: "flex-end", justifyContent: "center", gap: "4px", opacity: 0.15 }}>
                {[20,35,50,70,60,45,80,55,40,65,75,50,35,60,45,70,55,40,30,50].map((h, i) => (
                  <div key={i} style={{ width: "6px", height: `${h}px`, background: "#ef4444", borderRadius: "3px" }} />
                ))}
              </div>

              {/* Big play button */}
              <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
                <div
                  style={{
                    width: "80px", height: "80px", borderRadius: "50%",
                    background: "rgba(239,68,68,0.9)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 0 0 16px rgba(239,68,68,0.15), 0 8px 32px rgba(0,0,0,0.5)",
                    transition: "transform 0.15s, box-shadow 0.15s",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "scale(1.08)";
                    e.currentTarget.style.boxShadow = "0 0 0 20px rgba(239,68,68,0.2), 0 8px 32px rgba(0,0,0,0.6)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "0 0 0 16px rgba(239,68,68,0.15), 0 8px 32px rgba(0,0,0,0.5)";
                  }}
                >
                  <i className="fa-solid fa-play" style={{ fontSize: "30px", color: "#fff", marginLeft: "5px" }} />
                </div>
                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", fontWeight: "500", letterSpacing: "0.3px" }}>
                  Click to play recording
                </div>
              </div>
            </div>
          )}

          {/* Actual video player (shown after play is clicked) */}
          {!loading && !error && recordingUrl && isPlaying && (
            <video
              id="recording-video-player"
              ref={videoRef}
              src={recordingUrl}
              controls
              autoPlay
              playsInline
              style={{
                width: "100%", flex: 1,
                maxHeight: "calc(90vh - 130px)",
                display: "block", outline: "none", background: "#000",
              }}
              onPause={() => {/* keep isPlaying true — user can re-scrub */}}
              onError={() => {
                setIsPlaying(false);
                setError("Failed to play recording. The file may still be processing.");
              }}
            >
              Your browser does not support video playback.
            </video>
          )}
        </div>

        {/* ── Footer: expiry info ── */}
        {!loading && !error && recordingUrl && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "8px 16px",
            background: "rgba(255,255,255,0.02)",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            flexShrink: 0,
          }}>
            <span style={{ color: "rgba(255,255,255,0.25)", fontSize: "10px" }}>
              🔒 Secure streaming link
              {expiresAt ? ` · Expires ${new Date(expiresAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
            </span>
          </div>
        )}
      </div>

      {/* ── Keyframe animations ── */}
      <style>{`
        @keyframes fadeInBackdrop {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes slideUpModal {
          from { opacity: 0; transform: translate(-50%, -46%); }
          to   { opacity: 1; transform: translate(-50%, -50%); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
